import React, { useState } from "react";
import { Box, Typography, Button, CircularProgress, Alert } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import { createNewTenant } from "../api/apiClient";

// ── Tokens ───────────────────────────────────────────────────────────────────
const DS = {
  ink: "var(--color-ink)",
  ink2: "var(--color-ink-2)",
  ink3: "var(--color-ink-3)",
  ink4: "var(--color-ink-4)",
  line: "var(--color-hairline-cloud)",
  line2: "var(--color-surface-press-light)",
  surface: "#FFFFFF",
  surface2: "var(--color-surface-2)",
  surface3: "var(--color-surface-press-light)",
  danger: "#B42318",
  danger10: "rgba(180,35,24,.08)",
};

// ── Feature sets ─────────────────────────────────────────────────────────────
export const DELIVERY_FEATURES = [
  { key: "classes", label: "Classes", tag: "Core", desc: "Course and class management with scheduling, instructors, and enrollment tracking.", defaultOn: true },
  { key: "publicClassListing", label: "Public Class Listing", tag: "Public", desc: "Show available classes on a public-facing page without requiring a login.", defaultOn: true },
  { key: "instructorClassContent", label: "Instructor Content", tag: "Instructor", desc: "Instructors can upload skill sheets, tests, and documents to their classes.", defaultOn: true },
];
export const COMPLIANCE_FEATURES = [
  { key: "certManagement", label: "Certifications", tag: "Compliance", desc: "Track student certifications, expiration dates, and role compliance requirements.", defaultOn: false },
  { key: "certificationTrack", label: "Certification Track", tag: "Compliance", desc: "Show certification track progress and milestones on the student dashboard. Not fully implemented yet.", defaultOn: false },
  { key: "agencyManagement", label: "Agency Management", tag: "Admin", desc: "Agency admins manage departments, compliance, and COI documents for their members.", defaultOn: false },
  { key: "equipmentManagement", label: "Equipment Tracking", tag: "Operations", desc: "Track AEDs, gear, and other equipment across agencies and departments.", defaultOn: false },
];
export const ALL_FEATURES = [...DELIVERY_FEATURES, ...COMPLIANCE_FEATURES];

/**
 * Flips one feature. Certification track is built on Certifications: turning Certifications off also turns the track
 * off, and the track cannot be on without Certifications.
 */
export function applyFeatureToggle(prev, key) {
  const on = !prev[key];
  const next = { ...prev, [key]: on };
  if (key === "certManagement" && !on) next.certificationTrack = false;
  if (key === "certificationTrack" && on) next.certManagement = true;
  return next;
}

const STEP_LABELS = ["Identity", "Features", "Branding", "Profile Fields", "Review"];

// ── Stepper ──────────────────────────────────────────────────────────────────
function WizardStepper({ step, onJump }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimaryDk = theme.palette.primary.dark;
  const tPrimary50 = alpha(tPrimary, 0.10);
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start" }}>
      {STEP_LABELS.map((label, i) => (
        <React.Fragment key={i}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "7px", width: 68, flexShrink: 0 }}>
            <Box
              onClick={() => i < step && onJump(i)}
              sx={{
                width: 34, height: 34, borderRadius: "50%",
                border: `2px solid ${i <= step ? tPrimary : DS.line}`,
                bgcolor: i <= step ? tPrimary : DS.surface,
                display: "grid", placeItems: "center",
                fontSize: "12.5px", fontWeight: 700,
                color: i <= step ? "#fff" : DS.ink4,
                cursor: i < step ? "pointer" : "default",
                transition: "all .22s",
                ...(i === step ? { boxShadow: `0 0 0 5px ${tPrimary50}` } : {}),
                "&:hover": i < step ? { bgcolor: tPrimaryDk } : {},
              }}
            >
              {i < step ? (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : i + 1}
            </Box>
            <Typography sx={{ fontSize: "10.5px", fontWeight: 600, color: i <= step ? DS.ink2 : DS.ink4, whiteSpace: "nowrap", textAlign: "center", lineHeight: 1.3, display: { xs: "none", sm: "block" } }}>
              {label}
            </Typography>
          </Box>
          {i < STEP_LABELS.length - 1 && (
            <Box sx={{ flex: 1, height: 2, bgcolor: i < step ? tPrimary : DS.line, mt: "15px", transition: "background .3s" }} />
          )}
        </React.Fragment>
      ))}
    </Box>
  );
}

// ── Feature card ─────────────────────────────────────────────────────────────
function WizFeatureCard({ label, tag, desc, enabled, onToggle }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimary10 = alpha(tPrimary, 0.06);
  return (
    <Box
      onClick={onToggle}
      sx={{
        p: "15px 16px 17px",
        border: `1.5px solid ${enabled ? tPrimary : DS.line}`,
        borderRadius: "14px",
        bgcolor: enabled ? tPrimary10 : DS.surface2,
        cursor: "pointer", transition: "all .15s", userSelect: "none",
        "&:hover": { borderColor: enabled ? tPrimary : "var(--color-hairline-cool)", bgcolor: enabled ? tPrimary10 : "#fff", boxShadow: "0 1px 3px rgba(20,24,40,.06)" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: "9px" }}>
        <Box sx={{ width: 20, height: 20, borderRadius: "6px", border: `2px solid ${enabled ? tPrimary : DS.line2}`, bgcolor: enabled ? tPrimary : "#fff", display: "grid", placeItems: "center", color: enabled ? "#fff" : "transparent", transition: "all .15s", flexShrink: 0, mt: "1px" }}>
          {enabled && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
        </Box>
        <Box component="span" sx={{ fontSize: 10, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", px: "7px", py: "2px", borderRadius: "999px", bgcolor: enabled ? alpha(tPrimary, 0.08) : DS.surface3, color: enabled ? tPrimary : DS.ink4, border: `1px solid ${enabled ? "transparent" : DS.line2}` }}>
          {tag}
        </Box>
      </Box>
      <Typography sx={{ fontSize: "13.5px", fontWeight: 700, color: enabled ? tPrimary : DS.ink, mb: "5px", fontFamily: "var(--font-display)" }}>{label}</Typography>
      <Typography sx={{ fontSize: 12, color: DS.ink3, lineHeight: 1.5 }}>{desc}</Typography>
    </Box>
  );
}

// ── Section divider ───────────────────────────────────────────────────────────
function WizDivider({ label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", my: "2px" }}>
      <Box sx={{ flex: 1, height: 1, bgcolor: DS.line2 }} />
      <Typography sx={{ fontSize: "10.5px", fontWeight: 700, letterSpacing: ".09em", textTransform: "uppercase", color: DS.ink4, whiteSpace: "nowrap" }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, height: 1, bgcolor: DS.line2 }} />
    </Box>
  );
}

// ── Review section ────────────────────────────────────────────────────────────
function RvSection({ icon, title, onEdit, children }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimary10 = alpha(tPrimary, 0.06);
  const tPrimary50 = alpha(tPrimary, 0.10);
  return (
    <Box sx={{ bgcolor: DS.surface2, border: `1.5px solid ${DS.line2}`, borderRadius: "14px", overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: "11px", borderBottom: `1px solid ${DS.line2}`, bgcolor: "#fff" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 13, fontWeight: 700, color: DS.ink, fontFamily: "var(--font-display)" }}>
          <Box sx={{ width: 26, height: 26, borderRadius: "7px", bgcolor: tPrimary10, color: tPrimary, display: "grid", placeItems: "center", flexShrink: 0 }}>{icon}</Box>
          {title}
        </Box>
        <Box component="button" onClick={onEdit} sx={{ height: 28, px: "11px", borderRadius: "7px", border: `1.5px solid ${DS.line}`, bgcolor: "#fff", fontFamily: "inherit", fontSize: 12, fontWeight: 700, color: DS.ink3, cursor: "pointer", transition: "all .12s", "&:hover": { color: tPrimary, borderColor: tPrimary50, bgcolor: tPrimary10 } }}>
          Edit
        </Box>
      </Box>
      <Box sx={{ px: 2, py: "14px", display: "flex", flexDirection: "column", gap: "10px" }}>{children}</Box>
    </Box>
  );
}

function RvRow({ label, children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
      <Typography sx={{ color: DS.ink3, fontWeight: 500, minWidth: 96, flexShrink: 0, pt: "1px", fontSize: "13.5px" }}>{label}</Typography>
      <Box sx={{ color: DS.ink, fontWeight: 600, fontSize: "13.5px" }}>{children}</Box>
    </Box>
  );
}

// ── Shared field components ───────────────────────────────────────────────────
function FieldLabel({ children, optional }) {
  return (
    <Typography sx={{ fontSize: "11.5px", fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: DS.ink3, display: "flex", alignItems: "center", gap: "6px" }}>
      {children}
      {optional && <Box component="span" sx={{ fontSize: 11, fontWeight: 500, textTransform: "none", letterSpacing: 0, color: DS.ink4 }}>Optional</Box>}
    </Typography>
  );
}

function CtrlInput({ value, onChange, placeholder, onKeyDown, error, id }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  return (
    <Box
      component="input"
      id={id}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      onKeyDown={onKeyDown}
      autoComplete="off"
      sx={{
        height: 44, px: "14px", bgcolor: DS.surface2,
        border: `1.5px solid ${error ? DS.danger : DS.line}`,
        borderRadius: "12px", font: "inherit", fontSize: 14, fontWeight: 500, color: DS.ink,
        outline: "none", transition: "border-color .14s, box-shadow .14s", width: "100%",
        "&::placeholder": { color: DS.ink4, fontWeight: 400 },
        "&:focus": { borderColor: tPrimary, boxShadow: `0 0 0 3px ${alpha(tPrimary, 0.10)}`, bgcolor: "#fff" },
        ...(error ? { boxShadow: `0 0 0 3px rgba(180,35,24,.08)` } : {}),
      }}
    />
  );
}

// ── Card wrapper ────────────────────────────────────────────────────────────
function Card({ children }) {
  return (
    <Box sx={{ bgcolor: "#fff", border: `1px solid ${DS.line}`, borderRadius: "20px", boxShadow: "0 1px 3px rgba(20,24,40,.06)", overflow: "hidden" }}>
      <Box sx={{ p: "28px", display: "flex", flexDirection: "column", gap: "22px" }}>{children}</Box>
    </Box>
  );
}

function StepIntro({ title, desc }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 19, fontWeight: 800, letterSpacing: "-.025em", color: DS.ink, mb: "5px", fontFamily: "var(--font-display)" }}>{title}</Typography>
      <Typography sx={{ fontSize: "13.5px", color: DS.ink3, lineHeight: 1.6 }}>{desc}</Typography>
    </Box>
  );
}

// ── Main wizard ───────────────────────────────────────────────────────────────
export default function NewTenantWizard({ onComplete, onCancel }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimaryDk = theme.palette.primary.dark;
  const tPrimary10 = alpha(tPrimary, 0.06);
  const tPrimary50 = alpha(tPrimary, 0.10);
  const tSecondary = theme.palette.secondary.main;
  const tSecondary10 = alpha(tSecondary, 0.10);
  const [step, setStep] = useState(0);

  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [nameErr, setNameErr] = useState(false);

  const [features, setFeatures] = useState(
    Object.fromEntries(ALL_FEATURES.map((f) => [f.key, f.defaultOn]))
  );

  const [logo, setLogo] = useState("");
  const [icon, setIcon] = useState("");
  const [colors, setColors] = useState({ primary: "var(--color-primary)", secondary: "#15803D", accent: "#85651D" });

  const [fields, setFields] = useState([]);
  const [newField, setNewField] = useState("");

  const [creating, setCreating] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [createdTenant, setCreatedTenant] = useState(null);

  const initials = name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "?";

  const goNext = () => {
    if (step === 0 && !name.trim()) { setNameErr(true); return; }
    setStep((s) => s + 1);
  };
  // Certification track is built on Certifications: turning Certifications off also turns the track off, and the
  // track cannot be on without Certifications.
  const toggleFeature = (key) => setFeatures((p) => applyFeatureToggle(p, key));
  const goBack = () => setStep((s) => s - 1);
  const jumpTo = (idx) => { if (idx < step) setStep(idx); };

  const addField = () => {
    const f = newField.trim();
    if (!f) return;
    setFields((prev) => [...prev, f]);
    setNewField("");
  };

  const handleCreate = async () => {
    setCreating(true);
    setSubmitError("");
    try {
      const created = await createNewTenant({
        name: name.trim(),
        customDomain: domain.trim() || null,
        features,
        logo: logo || null,
        icon: icon || null,
        theme: colors,
        customFields: fields,
      });
      setCreatedTenant(created);
    } catch (e) {
      setSubmitError(e?.response?.data?.message || "Failed to create tenant");
    } finally {
      setCreating(false);
    }
  };

  const resetWizard = () => {
    setCreatedTenant(null);
    setStep(0);
    setName(""); setDomain(""); setNameErr(false);
    setFeatures(Object.fromEntries(ALL_FEATURES.map((f) => [f.key, f.defaultOn])));
    setLogo(""); setIcon(""); setColors({ primary: "var(--color-primary)", secondary: "#15803D", accent: "#85651D" });
    setFields([]); setNewField(""); setSubmitError("");
  };

  const enabledFeats = ALL_FEATURES.filter((f) => features[f.key]);

  // ── Success ─────────────────────────────────────────────────────────────────
  if (createdTenant) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: "64px", px: 3, gap: 2, textAlign: "center" }}>
        <Box sx={{ width: 72, height: 72, borderRadius: "20px", bgcolor: tSecondary10, border: `1px solid ${alpha(tSecondary, 0.20)}`, display: "grid", placeItems: "center", color: tSecondary }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
        </Box>
        <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: "-.03em", color: DS.ink, mt: 1, fontFamily: "var(--font-display)" }}>Tenant created!</Typography>
        <Typography sx={{ fontSize: 14, color: DS.ink3, maxWidth: 320, lineHeight: 1.65 }}>
          {createdTenant.name} is ready. Admins and users can now be added.
        </Typography>
        <Box sx={{ display: "flex", gap: "10px", mt: 1 }}>
          <Button onClick={resetWizard} sx={{ height: 40, px: "20px", borderRadius: "11px", textTransform: "none", fontWeight: 700, fontSize: 13, border: `1.5px solid ${DS.line}`, color: DS.ink3, bgcolor: "transparent", "&:hover": { bgcolor: DS.surface3 } }}>
            Set up another tenant
          </Button>
          <Button onClick={() => onComplete(createdTenant)} sx={{ height: 40, px: "20px", borderRadius: "11px", textTransform: "none", fontWeight: 700, fontSize: 13, bgcolor: tPrimary, color: "#fff", boxShadow: "none", "&:hover": { bgcolor: tPrimaryDk, boxShadow: "none" } }}>
            View Tenant
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", px: "24px", pb: "80px", pt: "40px" }}>
      <Box sx={{ width: "100%", maxWidth: 648 }}>

        {/* Header */}
        <Box sx={{ mb: "32px" }}>
          <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: "28px" }}>
            <Typography sx={{ fontSize: 23, fontWeight: 800, letterSpacing: "-.03em", color: DS.ink, fontFamily: "var(--font-display)" }}>New Tenant</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: DS.ink4 }}>Step {step + 1} of 5</Typography>
          </Box>
          <WizardStepper step={step} onJump={jumpTo} />
        </Box>

        {/* ═══ STEP 1: IDENTITY ═══ */}
        {step === 0 && (
          <Card>
            <StepIntro title="What's this tenant called?" desc="Give this tenant a name and optionally a custom domain URL. You can change these at any time." />
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: "18px 22px", bgcolor: name.trim() ? tPrimary10 : DS.surface2, border: `1.5px solid ${name.trim() ? tPrimary50 : DS.line2}`, borderRadius: "16px", transition: "all .2s" }}>
              <Box sx={{ width: 48, height: 48, borderRadius: "13px", bgcolor: tPrimary, display: "grid", placeItems: "center", fontSize: 14, fontWeight: 800, color: "#fff", letterSpacing: "-.02em", flexShrink: 0 }}>
                {initials}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: name.trim() ? DS.ink : DS.ink4, fontStyle: name.trim() ? "normal" : "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {name.trim() || "Tenant name will appear here"}
                </Typography>
                {domain.trim() && (
                  <Typography sx={{ fontSize: 12, color: DS.ink4, mt: "3px", fontFamily: "monospace" }}>↗ {domain.trim()}</Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
              <FieldLabel>Tenant Name <Box component="span" sx={{ color: "#B42318" }}>*</Box></FieldLabel>
              <CtrlInput value={name} onChange={(e) => { setName(e.target.value); if (nameErr && e.target.value.trim()) setNameErr(false); }} placeholder="e.g. South Branch Training Center" error={nameErr} />
              {nameErr && <Typography sx={{ fontSize: 12, color: DS.danger, fontWeight: 600 }}>Tenant name is required to continue</Typography>}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
              <Typography variant="body2">Certificates (including AHA eCards) are set up after onboarding, under Training Center › Certificates.</Typography>
              <FieldLabel optional>Custom Domain URL</FieldLabel>
              <CtrlInput value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="e.g. training.southbranch.gov" />
              <Typography sx={{ fontSize: 12, color: DS.ink3 }}>Hostname only — no https:// or trailing slash</Typography>
            </Box>
          </Card>
        )}

        {/* ═══ STEP 2: FEATURES ═══ */}
        {step === 1 && (
          <Card>
            <StepIntro title="What capabilities does this tenant need?" desc="Select the features to enable. You can turn any of these on or off later from the tenant's settings." />
            <WizDivider label="Course Delivery" />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "10px" }}>
              {DELIVERY_FEATURES.map((f) => (
                <WizFeatureCard key={f.key} label={f.label} tag={f.tag} desc={f.desc} enabled={!!features[f.key]} onToggle={() => toggleFeature(f.key)} />
              ))}
            </Box>
            <WizDivider label="Compliance & Operations" />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "10px" }}>
              {COMPLIANCE_FEATURES.map((f) => (
                <WizFeatureCard key={f.key} label={f.label} tag={f.tag} desc={f.desc} enabled={!!features[f.key]} onToggle={() => toggleFeature(f.key)} />
              ))}
            </Box>
          </Card>
        )}

        {/* ═══ STEP 3: BRANDING ═══ */}
        {step === 2 && (
          <Card>
            <StepIntro title="How should this tenant look?" desc="Add logos and pick brand colors. All of this can be updated later — skip ahead if you're not ready." />
            <WizDivider label="Logo & Icon" />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "18px" }}>
              {[{ key: "logo", val: logo, set: setLogo, hint: "Sidebar header" }, { key: "icon", val: icon, set: setIcon, hint: "Favicon / avatar" }].map(({ key, val, set, hint }) => (
                <Box key={key} sx={{ display: "flex", flexDirection: "column", gap: "9px" }}>
                  <FieldLabel>{key === "logo" ? "Logo URL" : "Icon URL"}</FieldLabel>
                  <CtrlInput value={val} onChange={(e) => set(e.target.value)} placeholder="/path/to/image.png" />
                  {val ? (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 76, bgcolor: DS.surface3, border: `1.5px dashed ${DS.line2}`, borderRadius: "12px" }}>
                      <img src={val} alt="" style={{ maxHeight: 60, maxWidth: key === "icon" ? 60 : 140, objectFit: "contain" }} />
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 76, bgcolor: DS.surface3, border: `1.5px dashed ${DS.line2}`, borderRadius: "12px", gap: "8px" }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: DS.ink4 }}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                      <Typography sx={{ fontSize: 12, color: DS.ink4, fontWeight: 500 }}>{hint}</Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
            <WizDivider label="Color Scheme" />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: "14px" }}>
              {[{ key: "primary", label: "Primary" }, { key: "secondary", label: "Secondary" }, { key: "accent", label: "Accent" }].map(({ key, label }) => (
                <Box key={key}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: DS.ink4, mb: 1 }}>{label}</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box component="input" type="color" value={colors[key]} onChange={(e) => setColors((p) => ({ ...p, [key]: e.target.value }))} sx={{ width: 44, height: 44, flexShrink: 0, border: `1.5px solid ${DS.line}`, borderRadius: "10px", cursor: "pointer", p: "3px", bgcolor: DS.surface2, "&::-webkit-color-swatch-wrapper": { padding: 0, borderRadius: "6px" }, "&::-webkit-color-swatch": { border: "none", borderRadius: "6px" } }} />
                    <Box component="input" type="text" value={colors[key]} maxLength={7} onChange={(e) => { if (/^#[0-9A-Fa-f]{0,6}$/.test(e.target.value)) setColors((p) => ({ ...p, [key]: e.target.value })); }} sx={{ flex: 1, height: 44, px: "12px", bgcolor: DS.surface2, border: `1.5px solid ${DS.line}`, borderRadius: "12px", fontFamily: "monospace", fontSize: 13, fontWeight: 600, color: DS.ink, outline: "none", "&:focus": { borderColor: tPrimary, boxShadow: `0 0 0 3px ${tPrimary50}`, bgcolor: "#fff" } }} />
                  </Box>
                  <Box sx={{ height: 5, borderRadius: "999px", mt: "8px", bgcolor: /^#[0-9A-Fa-f]{6}$/.test(colors[key]) ? colors[key] : DS.line }} />
                </Box>
              ))}
            </Box>
          </Card>
        )}

        {/* ═══ STEP 4: PROFILE FIELDS ═══ */}
        {step === 3 && (
          <Card>
            <StepIntro title="Any custom profile fields?" desc="These appear on user profiles within this tenant — useful for things like Badge Number, Department, or Station. You can skip this step and add fields later." />
            {fields.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {fields.map((f, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", gap: "10px", p: "11px 14px", bgcolor: DS.surface2, border: `1.5px solid ${DS.line}`, borderRadius: "12px" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: DS.ink4, flexShrink: 0 }} strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="6" r="1" fill="currentColor"/><circle cx="15" cy="6" r="1" fill="currentColor"/><circle cx="9" cy="12" r="1" fill="currentColor"/><circle cx="15" cy="12" r="1" fill="currentColor"/><circle cx="9" cy="18" r="1" fill="currentColor"/><circle cx="15" cy="18" r="1" fill="currentColor"/></svg>
                    <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 600, color: DS.ink }}>{f}</Typography>
                    <Box component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".04em", px: "8px", py: "3px", borderRadius: "999px", bgcolor: DS.surface3, color: DS.ink4, border: `1px solid ${DS.line2}` }}>Text</Box>
                    <Box component="button" onClick={() => setFields((p) => p.filter((_, j) => j !== i))} sx={{ width: 32, height: 32, borderRadius: "8px", border: `1.5px solid ${DS.line}`, bgcolor: "#fff", display: "grid", placeItems: "center", cursor: "pointer", color: DS.ink3, flexShrink: 0, ml: "auto", transition: "all .12s", "&:hover": { bgcolor: DS.danger10, color: DS.danger, borderColor: "rgba(180,35,24,.18)" } }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
            <Box sx={{ display: "flex", gap: "8px" }}>
              <CtrlInput value={newField} onChange={(e) => setNewField(e.target.value)} placeholder="Field name (e.g. Badge Number, Station)" onKeyDown={(e) => e.key === "Enter" && addField()} />
              <Button onClick={addField} sx={{ height: 44, px: "16px", borderRadius: "12px", textTransform: "none", fontWeight: 700, fontSize: 13, bgcolor: DS.surface2, color: DS.ink2, border: `1.5px solid ${DS.line}`, flexShrink: 0, "&:hover": { bgcolor: DS.surface3 } }}>
                <AddIcon sx={{ fontSize: 16, mr: 0.5 }} />Add
              </Button>
            </Box>
          </Card>
        )}

        {/* ═══ STEP 5: REVIEW ═══ */}
        {step === 4 && (
          <Card>
            <StepIntro title="Ready to launch?" desc="Review your configuration. Click any section to edit before creating the tenant." />
            {submitError && <Alert severity="error" sx={{ borderRadius: "10px" }}>{submitError}</Alert>}
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <RvSection icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>} title="Identity" onEdit={() => jumpTo(0)}>
                <RvRow label="Name">{name.trim() || "—"}</RvRow>
                <RvRow label="Domain"><Typography component="span" sx={{ fontSize: "13.5px", fontWeight: domain.trim() ? 600 : 400, color: domain.trim() ? DS.ink : DS.ink4, fontStyle: domain.trim() ? "normal" : "italic" }}>{domain.trim() || "Not set"}</Typography></RvRow>
              </RvSection>
              <RvSection icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>} title="Features" onEdit={() => jumpTo(1)}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {enabledFeats.length > 0 ? enabledFeats.map((f) => (
                    <Box key={f.key} component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: 12, fontWeight: 600, px: "10px", py: "4px", borderRadius: "999px", bgcolor: tPrimary10, color: tPrimary, border: `1px solid ${tPrimary50}` }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      {f.label}
                    </Box>
                  )) : <Typography sx={{ fontSize: "13.5px", color: DS.ink4, fontStyle: "italic" }}>No features enabled</Typography>}
                </Box>
              </RvSection>
              <RvSection icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg>} title="Branding" onEdit={() => jumpTo(2)}>
                <RvRow label="Colors">
                  <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    {["primary", "secondary", "accent"].map((k) => (
                      <Box key={k} sx={{ width: 18, height: 18, borderRadius: "50%", bgcolor: colors[k], border: "2.5px solid #fff", boxShadow: "0 1px 3px rgba(0,0,0,.15)" }} />
                    ))}
                    <Typography sx={{ fontSize: 12, color: DS.ink4, ml: "4px" }}>Primary · Secondary · Accent</Typography>
                  </Box>
                </RvRow>
              </RvSection>
              <RvSection icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>} title="Profile Fields" onEdit={() => jumpTo(3)}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {fields.length > 0 ? fields.map((f, i) => (
                    <Box key={i} component="span" sx={{ fontSize: 12, fontWeight: 600, px: "10px", py: "4px", borderRadius: "999px", bgcolor: DS.surface3, color: DS.ink2, border: `1px solid ${DS.line2}` }}>{f}</Box>
                  )) : <Typography sx={{ fontSize: "13.5px", color: DS.ink4, fontStyle: "italic" }}>No custom fields</Typography>}
                </Box>
              </RvSection>
            </Box>
            <Button onClick={handleCreate} disabled={creating} fullWidth sx={{ height: 54, borderRadius: "14px", textTransform: "none", fontSize: 16, fontWeight: 800, bgcolor: tPrimary, color: "#fff", letterSpacing: "-.01em", boxShadow: `0 6px 20px -6px ${alpha(tPrimary, 0.5)}`, "&:hover": { bgcolor: tPrimaryDk, boxShadow: `0 10px 28px -6px ${alpha(tPrimary, 0.55)}`, transform: "translateY(-1px)" }, "&.Mui-disabled": { bgcolor: DS.line, color: DS.ink4, boxShadow: "none" }, transition: "all .15s" }}>
              {creating ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : (
                <><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 10 }}><path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4 20-7z"/></svg>Create Tenant</>
              )}
            </Button>
          </Card>
        )}

        {/* Nav buttons */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2 }}>
          <Button onClick={step === 0 ? onCancel : goBack} sx={{ height: 40, px: "20px", borderRadius: "11px", textTransform: "none", fontWeight: 700, fontSize: 13, border: `1.5px solid ${DS.line}`, color: DS.ink3, bgcolor: "transparent", "&:hover": { bgcolor: DS.surface3 } }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}><polyline points="15 18 9 12 15 6"/></svg>
            {step === 0 ? "Cancel" : "Back"}
          </Button>
          {step < 4 && (
            <Button onClick={goNext} disabled={step === 0 && !name.trim()} sx={{ height: 40, px: "20px", borderRadius: "11px", textTransform: "none", fontWeight: 700, fontSize: 13, bgcolor: tPrimary, color: "#fff", boxShadow: `0 4px 12px -4px ${alpha(tPrimary, 0.45)}`, "&:hover:not(:disabled)": { bgcolor: tPrimaryDk }, "&.Mui-disabled": { opacity: 0.38, cursor: "not-allowed", boxShadow: "none" } }}>
              {step === 3 ? "Review →" : "Continue"}
              {step < 3 && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 6 }}><polyline points="9 18 15 12 9 6"/></svg>}
            </Button>
          )}
        </Box>

      </Box>
    </Box>
  );
}
